import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Power, Search } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import IndustryModal from './components/IndustryModal';
import { industryService } from '../../services/industryService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import { getIndustryConfetti } from '../../utils/industryConfettiConfig';
import * as LucideIcons from 'lucide-react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';



export default function IndustryManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [industries, setIndustries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  useEffect(() => {
    loadIndustries();
  }, []);

  const loadIndustries = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await industryService?.getAll();
      
      // Deserialize confetti icons (convert icon names back to components)
      const industriesWithIcons = data?.map(industry => {
        if (industry?.confettiIcons && Array.isArray(industry?.confettiIcons)) {
          const deserializedIcons = industry?.confettiIcons?.map(iconData => {
            const IconComponent = LucideIcons?.[iconData?.iconName] || LucideIcons?.Circle;
            return {
              icon: IconComponent,
              color: iconData?.color,
              label: iconData?.label
            };
          });
          return { ...industry, confettiIcons: deserializedIcons };
        }
        return industry;
      });
      
      setIndustries(industriesWithIcons || []);
    } catch (err) {
      setError(err?.message || 'Failed to load industries');
      console.error('Error loading industries:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredIndustries = industries?.filter(industry =>
    searchQuery === '' ||
    industry?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    industry?.naicsCode?.includes(searchQuery) ||
    industry?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleAdd = () => {
    setSelectedIndustry(null);
    setShowModal(true);
  };

  const handleEdit = (industry) => {
    setSelectedIndustry(industry);
    setShowModal(true);
  };

  const handleDelete = async (industryId) => {
    if (!window.confirm('Are you sure you want to delete this industry? This will affect any companies using this industry.')) {
      return;
    }

    try {
      await industryService?.delete(industryId);
      loadIndustries();
    } catch (err) {
      console.error('Error deleting industry:', err);
      setError(err?.message);
    }
  };

  const handleToggleStatus = async (industryId, currentStatus) => {
    try {
      await industryService?.toggleStatus(industryId, currentStatus);
      loadIndustries();
    } catch (err) {
      console.error('Error toggling industry status:', err);
      setError(err?.message);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedIndustry(null);
    loadIndustries();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <ComponentIdBadge id="industry-management" />
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Industry Management</h1>
              <p className="text-gray-600">Manage company industries and niches available in the system</p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            {/* Controls */}
            <div className="mb-6 flex items-center justify-between gap-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search industries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Button onClick={handleAdd}>
                <Plus className="w-4 h-4 mr-2" />
                Add Industry
              </Button>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading industries...</p>
              </div>
            )}

            {/* Industries Table */}
            {!loading && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Industry
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        NAICS Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Icon
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Confetti Preview
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sort Order
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredIndustries?.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                          {searchQuery ? 'No industries found matching your search' : 'No industries yet. Add your first industry to get started.'}
                        </td>
                      </tr>
                    ) : (
                      filteredIndustries?.map((industry) => {
                        const confettiIcons = industry?.confettiIcons?.length > 0 
                          ? industry?.confettiIcons 
                          : getIndustryConfetti(industry?.naicsCode);
                        
                        return (
                          <tr key={industry?.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div
                                  className="w-8 h-8 rounded-lg flex items-center justify-center mr-3"
                                  style={{ backgroundColor: `${industry?.color}20` }}
                                >
                                  <span style={{ color: industry?.color }} className="text-sm font-semibold">
                                    {industry?.name?.charAt(0)}
                                  </span>
                                </div>
                                <div className="text-sm font-medium text-gray-900">{industry?.name}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded inline-block">
                                {industry?.naicsCode || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-600">{industry?.description || '-'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{industry?.icon}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-1 max-w-xs">
                                {confettiIcons?.slice(0, 6)?.map((iconData, index) => {
                                  const IconComponent = iconData?.icon;
                                  return (
                                    <div
                                      key={`${iconData?.label}-${index}`}
                                      className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded border border-gray-200"
                                      title={iconData?.label}
                                    >
                                      {IconComponent && (
                                        <IconComponent
                                          className="w-4 h-4"
                                          style={{ color: iconData?.color }}
                                          strokeWidth={2}
                                        />
                                      )}
                                      <span className="text-xs text-gray-600">{iconData?.label}</span>
                                    </div>
                                  );
                                })}
                                {confettiIcons?.length > 6 && (
                                  <div className="flex items-center px-2 py-1 text-xs text-gray-500">
                                    +{confettiIcons?.length - 6} more
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{industry?.sortOrder}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  industry?.isActive
                                    ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {industry?.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleToggleStatus(industry?.id, industry?.isActive)}
                                  className="text-gray-600 hover:text-gray-900"
                                  title={industry?.isActive ? 'Deactivate' : 'Activate'}
                                >
                                  <Power className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleEdit(industry)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(industry?.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
      {/* Industry Modal */}
      {showModal && (
        <IndustryModal
          industry={selectedIndustry}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}